<template>
  <div class="contract-view-container" v-loading="loading">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>合同详情 - {{ contractInfo.contractName || '加载中...' }}</span>
      </div>
      <div class="header-actions">
        <el-button 
          v-if="canConfirmContract()"
          v-permission="'contracts:contract-view:confirm'"
          type="success" 
          icon="el-icon-check" 
          @click="confirmContract" 
          :disabled="loading"
        >确认合同</el-button>
        <el-button v-if="canReviewContract" v-permission="'contracts:contract-view:review'" type="primary" icon="el-icon-check" @click="review(true)">复核通过</el-button>
        <el-button v-if="canReviewContract" v-permission="'contracts:contract-view:review'" type="warning" icon="el-icon-back" @click="review(false)">退回修改</el-button>
        <el-button
          v-if="canDownloadContractAttachment"
          type="info"
          icon="el-icon-paperclip"
          @click="attachmentDialogVisible = true"
          :disabled="loading"
        >附件</el-button>
        <el-button v-permission="'contracts:contract-view:print'" type="primary" icon="el-icon-printer" @click="printContract" :disabled="loading">打印合同</el-button>
        <el-button @click="goBack">返回</el-button>
      </div>
    </div>

    <!-- 打印内容区域 -->
    <div class="print-content" ref="printContent">

    <!-- 合同基本信息 -->
    <div class="info-section">
      <h3 class="section-title">基本信息</h3>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>合同名称：</label>
            <span>{{ contractInfo.contractName || '未设置' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>合同编号：</label>
            <span>{{ contractInfo.contractNumber || '未生成' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>合同状态：</label>
            <el-tag :type="getStatusTagType(contractInfo.status)">
              {{ getStatusText(contractInfo.status) }}
            </el-tag>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>合同单位：</label>
            <span>{{ getContractUnitName() }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>供方单位：</label>
            <span>{{ getSupplierUnitName() }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>需求方单位：</label>
            <span>{{ getCustomerUnitNames() }}</span>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>关联项目：</label>
            <span>{{ getProjectNames() }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>创建时间：</label>
            <span>{{ formatDate(contractInfo.createTime) }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>合同金额：</label>
            <span class="amount-text">{{ formatAmount(contractInfo.contractAmount) }}</span>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>合同联系人：</label>
            <span>{{ maskName(contractInfo.contactPerson) || '未设置' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>联系电话：</label>
            <span>{{ maskPhone(contractInfo.contactPhone) || '未设置' }}</span>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 合同详情 -->
    <div class="info-section">
      <h3 class="section-title">合同详情</h3>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>计价方式：</label>
            <el-tag :type="contractInfo.pricingMethod === 'FIXED' ? 'success' : 'warning'">
              {{ contractInfo.pricingMethod === 'FIXED' ? '固定价' : '变动价' }}
            </el-tag>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>是否计息：</label>
            <span>{{ contractInfo.interestCalculation ? '是' : '否' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>结算方式：</label>
            <span>{{ contractInfo.settlementMethod || '未设置' }}</span>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>付款天数：</label>
            <span>{{ getPaymentDaysDisplay() }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>免息天数：</label>
            <span>{{ contractInfo.interestFreeDays || 0 }}天</span>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>利率类型：</label>
            <span>{{ contractInfo.interestRateType === 'FIXED' ? '固定利率' : '变动利率' }}</span>
          </div>
        </el-col>
        <el-col :span="8" v-if="contractInfo.interestRateType === 'FIXED'">
          <div class="info-item">
            <label>固定利率：</label>
            <span>{{ contractInfo.fixedInterestRate || 0 }}%</span>
          </div>
        </el-col>
        <el-col :span="8" v-if="contractInfo.interestRateType === 'VARIABLE'">
          <div class="info-item">
            <label>变动利率档数：</label>
            <span>{{ contractInfo.variableInterestRates ? contractInfo.variableInterestRates.length : 0 }}档</span>
          </div>
        </el-col>
      </el-row>
      
      <!-- 变动利率详情 -->
      <div v-if="contractInfo.interestRateType === 'VARIABLE' && contractInfo.variableInterestRates && contractInfo.variableInterestRates.length > 0" class="variable-rates-section">
        <h4>变动利率详情</h4>
        <el-table :data="contractInfo.variableInterestRates" border style="width: 100%">
          <el-table-column label="开始天数" prop="startDays" width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.startDays }}天
            </template>
          </el-table-column>
          <el-table-column label="结束天数" prop="endDays" width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.endDays }}天
            </template>
          </el-table-column>
          <el-table-column label="利率" prop="rate" width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.rate }}%
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 合同清单 -->
    <div class="info-section">
      <h3 class="section-title">合同清单</h3>
      <div v-if="contractInfo.contractItems && contractInfo.contractItems.length > 0">
        <!-- 固定价格式表格 -->
        <el-table 
          v-if="contractInfo.pricingMethod === 'FIXED'"
          :data="contractInfo.contractItems" 
          border 
          style="width: 100%" 
          class="contract-items-table"
        >
          <el-table-column label="序号" prop="sequenceNumber" min-width="80" align="center"></el-table-column>
          <el-table-column label="材料名称" prop="productName" min-width="200" align="center"></el-table-column>
          <el-table-column label="规格型号" prop="specification" min-width="200" align="center">
            <template slot-scope="scope">
              <el-tooltip
                v-if="scope.row.specification"
                :content="scope.row.specification"
                placement="top"
                effect="dark"
                popper-class="spec-tooltip"
              >
                <span>{{ truncateText(scope.row.specification, 20) }}</span>
              </el-tooltip>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="材质要求" prop="materialRequirement" min-width="150" align="center">
            <template slot-scope="scope">
              <el-tooltip
                v-if="scope.row.materialRequirement && scope.row.materialRequirement.length > 20"
                :content="scope.row.materialRequirement"
                placement="top"
                effect="dark"
                popper-class="spec-tooltip"
              >
                <span>{{ truncateText(scope.row.materialRequirement, 20) }}</span>
              </el-tooltip>
              <span v-else>{{ scope.row.materialRequirement || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="单位" prop="unit" min-width="100" align="center"></el-table-column>
          <el-table-column label="数量" prop="quantity" min-width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.quantity }}
            </template>
          </el-table-column>
          <el-table-column label="不含税单价(元)" prop="taxExclusiveUnitPrice" min-width="150" align="center">
            <template slot-scope="scope">
              {{ formatUnitPrice(scope.row.taxExclusiveUnitPrice) }}
            </template>
          </el-table-column>
          <el-table-column label="含税单价(元)" prop="taxInclusiveUnitPrice" min-width="150" align="center">
            <template slot-scope="scope">
              {{ formatUnitPrice(scope.row.taxInclusiveUnitPrice) }}
            </template>
          </el-table-column>
          <el-table-column label="含税合价(元)" prop="taxInclusiveTotalAmount" min-width="150" align="center">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.taxInclusiveTotalAmount) }}
            </template>
          </el-table-column>
          <el-table-column label="税率" prop="taxRate" min-width="100" align="center">
            <template slot-scope="scope">
              {{ scope.row.taxRate || 0 }}%
            </template>
          </el-table-column>
          <el-table-column label="备注" prop="remark" min-width="150" align="center"></el-table-column>
        </el-table>

        <!-- 变动价格式表格 -->
        <el-table 
          v-else
          :data="contractInfo.contractItems" 
          border 
          style="width: 100%" 
          class="contract-items-table"
        >
          <el-table-column label="序号" prop="sequenceNumber" min-width="80" align="center"></el-table-column>
          <el-table-column label="材料名称" prop="productName" min-width="200" align="center"></el-table-column>
          <el-table-column label="规格型号" prop="specification" min-width="200" align="center">
            <template slot-scope="scope">
              <el-tooltip
                v-if="scope.row.specification"
                :content="scope.row.specification"
                placement="top"
                effect="dark"
                popper-class="spec-tooltip"
              >
                <span>{{ truncateText(scope.row.specification, 20) }}</span>
              </el-tooltip>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="材质要求" prop="materialRequirement" min-width="150" align="center">
            <template slot-scope="scope">
              <el-tooltip
                v-if="scope.row.materialRequirement && scope.row.materialRequirement.length > 20"
                :content="scope.row.materialRequirement"
                placement="top"
                effect="dark"
                popper-class="spec-tooltip"
              >
                <span>{{ truncateText(scope.row.materialRequirement, 20) }}</span>
              </el-tooltip>
              <span v-else>{{ scope.row.materialRequirement || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="单位" prop="unit" min-width="100" align="center"></el-table-column>
          <el-table-column label="数量" prop="quantity" min-width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.quantity }}
            </template>
          </el-table-column>
          <el-table-column label="含税固定价(元)" prop="taxInclusiveBasePrice" min-width="150" align="center">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.taxInclusiveBasePrice) }}
            </template>
          </el-table-column>
          <el-table-column label="含税变动价(元)" prop="taxInclusiveFloatingPrice" min-width="150" align="center">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.taxInclusiveFloatingPrice) }}
            </template>
          </el-table-column>
          <el-table-column label="含税合价(元)" prop="taxInclusiveTotalAmount" min-width="150" align="center">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.taxInclusiveTotalAmount) }}
            </template>
          </el-table-column>
          <el-table-column label="税率" prop="taxRate" min-width="100" align="center">
            <template slot-scope="scope">
              {{ scope.row.taxRate || 0 }}%
            </template>
          </el-table-column>
          <el-table-column label="备注" prop="remark" min-width="150" align="center"></el-table-column>
        </el-table>
        
        <!-- 合计信息 -->
        <div class="total-section">
          <div class="total-item">
            <label>总数量：</label>
            <span>{{ getTotalQuantity() }}</span>
          </div>
          <div class="total-item">
            <label>合同总金额：</label>
            <span class="total-amount">{{ formatAmount(contractInfo.contractAmount) }}</span>
          </div>
        </div>
      </div>
      <div v-else class="no-data">
        <el-empty description="暂无合同清单"></el-empty>
      </div>
    </div>

    <!-- 合同参与方 -->
    <div class="info-section">
      <h3 class="section-title">合同参与方</h3>
             <div v-if="getAllParties().length > 0">
         <el-table :data="getAllParties()" border style="width: 100%" class="parties-table">
           <el-table-column label="序号" type="index" min-width="80" align="center"></el-table-column>
           <el-table-column label="参与方类型" prop="partyType" min-width="150" align="center">
             <template slot-scope="scope">
               <el-tag :type="getPartyTypeTagType(scope.row.partyType)">
                 {{ getPartyTypeText(scope.row.partyType) }}
               </el-tag>
             </template>
           </el-table-column>
           <el-table-column label="单位名称" min-width="400" align="center">
             <template slot-scope="scope">
               {{ getPartyUnitName(scope.row) }}
             </template>
           </el-table-column>
           <el-table-column label="备注" prop="remark" min-width="250" align="center"></el-table-column>
         </el-table>
       </div>
      <div v-else class="no-data">
        <el-empty description="暂无参与方信息"></el-empty>
      </div>
    </div>
    
    <!-- 打印内容区域结束 -->
    </div>

    <el-dialog
      v-dialog-drag
      title="合同附件"
      :visible.sync="attachmentDialogVisible"
      width="760px"
      :close-on-click-modal="false"
    >
      <contract-attachments :contract-id="contractId" readonly />
      <div slot="footer" class="dialog-footer">
        <el-button @click="attachmentDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getContractById } from '@/api/modules/contracts.js';
import { getAllUnits } from '@/api/modules/unit.js';
import { getProjectsByUnit } from '@/api/modules/projects.js';
import { updateContractStatus, reviewContract } from '@/api/modules/contracts.js';
import moment from 'moment';
import { getUserInfo } from '@/utils/auth';
import { maskName as maskNameUtil, maskPhone as maskPhoneUtil } from '@/utils/mask.js';
import { installPlatformPrintFooter } from '@/utils/print-footer';
import ContractAttachments from '@/components/ContractAttachments';

export default {
  name: 'ContractView',
  components: {
    ContractAttachments
  },
  data() {
    return {
      contractId: null,
      contractInfo: {},
      unitOptions: [],
      projectOptions: [],
      loading: false,
      attachmentDialogVisible: false
    }
  },
  computed: {
    canReviewContract() {
      if (this.contractInfo.status !== 'PENDING_REVIEW' || !this.contractInfo.reviewerId) return false;
      const currentUser = getUserInfo();
      const currentUserId = currentUser?.userId || currentUser?.id || currentUser?.user_id;
      return Number(currentUserId) === Number(this.contractInfo.reviewerId);
    },
    canDownloadContractAttachment() {
      const permissionCodes = this.$store.state.permissionCodes || [];
      return permissionCodes.includes('contracts:contract-view:download-attachment') ||
        permissionCodes.includes('contracts:contracts-list:download-scan');
    }
  },
  created() {
    this.contractId = this.$route.params.id;
    if (this.contractId) {
      this.fetchContractDetail();
      this.initData();
    }
  },
  methods: {
    // 获取合同详情
    async fetchContractDetail() {
      try {
        this.loading = true;
        const response = await getContractById(this.contractId);
        if (response.data && response.data.success) {
          this.contractInfo = response.data.data;
} else {
          this.$message.error('获取合同详情失败');
        }
      } catch (error) {
        console.error('获取合同详情失败:', error);
        this.$message.error('获取合同详情失败');
      } finally {
        this.loading = false;
      }
    },

    // 初始化数据
    async initData() {
      await this.fetchUnits();
      await this.fetchProjects();
    },

    // 获取单位列表
    async fetchUnits() {
      try {
        const response = await getAllUnits();
        if (response.data && response.data.success) {
          this.unitOptions = response.data.data || [];
        }
      } catch (error) {
        console.error('获取单位数据失败:', error);
      }
    },

    // 获取项目列表
    async fetchProjects() {
      try {
        const response = await getProjectsByUnit(1); // 暂时使用默认单位ID
        if (response.data && response.data.success) {
          this.projectOptions = response.data.data || [];
        }
      } catch (error) {
        console.error('获取项目数据失败:', error);
      }
    },

         // 获取合同单位名称
     getContractUnitName() {
       if (this.contractInfo.contractUnit && this.contractInfo.contractUnit.unitName) {
         return this.contractInfo.contractUnit.unitName;
       }
       if (this.contractInfo.contractUnitId) {
         const unit = this.unitOptions.find(u => u.id === this.contractInfo.contractUnitId);
         return unit ? (unit.unitName || unit.unit_name) : '未知单位';
       }
       // 合同单位应该是合同的责任单位，不是主要客户
       if (this.contractInfo.contractParties) {
         const contractUnit = this.contractInfo.contractParties.find(party => 
           party.partyType === 'CONTRACT_UNIT'
         );
         if (contractUnit && contractUnit.unitInfo) {
           return contractUnit.unitInfo.unitName;
         }
       }
       return '未设置';
     },

    // 获取供方单位名称
    getSupplierUnitName() {
      if (this.contractInfo.supplierUnit && this.contractInfo.supplierUnit.unitName) {
        return this.contractInfo.supplierUnit.unitName;
      }
      if (this.contractInfo.supplierUnitId) {
        const unit = this.unitOptions.find(u => u.id === this.contractInfo.supplierUnitId);
        return unit ? (unit.unitName || unit.unit_name) : '未知单位';
      }
      if (this.contractInfo.contractParties) {
        const supplier = this.contractInfo.contractParties.find(party => party.partyType === 'SUPPLIER');
        return supplier && supplier.unitInfo ? supplier.unitInfo.unitName : '未设置';
      }
      return '未设置';
    },

    // 获取需求方单位名称
    getCustomerUnitNames() {
      if (this.contractInfo.demandUnits && this.contractInfo.demandUnits.length > 0) {
        return this.contractInfo.demandUnits.map(unit => unit.unitName).join('、');
      }
      if (this.contractInfo.demandUnitIds && this.contractInfo.demandUnitIds.length > 0) {
        return this.contractInfo.demandUnitIds.map(id => {
          const unit = this.unitOptions.find(u => u.id === id);
          return unit ? (unit.unitName || unit.unit_name) : '未知单位';
        }).join('、');
      }
      if (this.contractInfo.contractParties) {
        const customers = this.contractInfo.contractParties.filter(party => party.partyType === 'CUSTOMER');
        if (customers.length > 0) {
          return customers.map(customer => 
            customer.unitInfo ? customer.unitInfo.unitName : '未知单位'
          ).join('、');
        }
      }
      return '未设置';
    },

    // 获取项目名称
    getProjectNames() {
      if (this.contractInfo.projects && this.contractInfo.projects.length > 0) {
        return this.contractInfo.projects.map(project => project.projectName).join('、');
      }
      if (this.contractInfo.projectIds && this.contractInfo.projectIds.length > 0) {
        return this.contractInfo.projectIds.map(id => {
          const project = this.projectOptions.find(p => p.id === id);
          return project ? project.projectName : '未知项目';
        }).join('、');
      }
      return '无关联项目';
    },

         // 获取所有参与方（只显示核心三个）
     getAllParties() {
       const parties = [];
       
               // 1. 合同单位
        if (this.contractInfo.contractUnit && this.contractInfo.contractUnit.unitName) {
          parties.push({
            partyType: 'CONTRACT_UNIT',
            unitInfo: { unitName: this.contractInfo.contractUnit.unitName },
            remark: '合同责任单位'
          });
        } else if (this.contractInfo.contractUnitId) {
          const unit = this.unitOptions.find(u => u.id === this.contractInfo.contractUnitId);
          if (unit) {
            parties.push({
              partyType: 'CONTRACT_UNIT',
              unitInfo: { unitName: unit.unitName || unit.unit_name },
              remark: '合同责任单位'
            });
          }
        }
        
        // 2. 供应商
        if (this.contractInfo.supplierUnit && this.contractInfo.supplierUnit.unitName) {
          parties.push({
            partyType: 'SUPPLIER',
            unitInfo: { unitName: this.contractInfo.supplierUnit.unitName },
            remark: '供应商'
          });
        } else if (this.contractInfo.supplierUnitId) {
          const unit = this.unitOptions.find(u => u.id === this.contractInfo.supplierUnitId);
          if (unit) {
            parties.push({
              partyType: 'SUPPLIER',
              unitInfo: { unitName: unit.unitName || unit.unit_name },
              remark: '供应商'
            });
          }
        }
        
        // 3. 客户单位
        if (this.contractInfo.demandUnits && this.contractInfo.demandUnits.length > 0) {
          this.contractInfo.demandUnits.forEach((unit) => {
            parties.push({
              partyType: 'CUSTOMER',
              unitInfo: { unitName: unit.unitName },
              remark: '客户单位'
            });
          });
        } else if (this.contractInfo.demandUnitIds && this.contractInfo.demandUnitIds.length > 0) {
          this.contractInfo.demandUnitIds.forEach((id) => {
            const unit = this.unitOptions.find(u => u.id === id);
            if (unit) {
              parties.push({
                partyType: 'CUSTOMER',
                unitInfo: { unitName: unit.unitName || unit.unit_name },
                remark: '客户单位'
              });
            }
          });
        }
       
       return parties;
     },

     // 获取参与方单位名称
     getPartyUnitName(party) {
       if (party.unitInfo && party.unitInfo.unitName) {
         return party.unitInfo.unitName;
       }
       if (party.unitInfoId) {
         const unit = this.unitOptions.find(u => u.id === party.unitInfoId);
         return unit ? (unit.unitName || unit.unit_name) : '未知单位';
       }
       return '未知单位';
     },

     // 获取参与方类型标签类型
     getPartyTypeTagType(partyType) {
       const typeMap = {
         'CONTRACT_UNIT': 'primary',    // 蓝色 - 合同单位
         'SUPPLIER': 'warning',         // 橙色 - 供应商
         'CUSTOMER': 'success'          // 绿色 - 客户
       };
       return typeMap[partyType] || 'info';
     },

     // 获取参与方类型文本
     getPartyTypeText(partyType) {
       const typeMap = {
         'CONTRACT_UNIT': '合同单位',
         'SUPPLIER': '供应商',
         'CUSTOMER': '客户单位'
       };
       return typeMap[partyType] || partyType;
     },

    // 获取总数量
    getTotalQuantity() {
      if (!this.contractInfo.contractItems) return 0;
      return this.contractInfo.contractItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    },

    // 获取付款天数显示（显示所有付款天数）
    getPaymentDaysDisplay() {
      if (!this.contractInfo.paymentDays || !Array.isArray(this.contractInfo.paymentDays) || this.contractInfo.paymentDays.length === 0) {
        return '未设置';
      }
      
      // 格式化每个付款天数项
      const formattedItems = this.contractInfo.paymentDays.map(item => {
        let days = '';
        const percentage = item.percentage || 0;
        
        // 优先使用days字段（旧格式，请求时使用）
        if (item.days !== null && item.days !== undefined && item.days > 0) {
          days = `${item.days}天`;
        }
        // 使用startDays和endDays字段（新格式，返回时使用）
        else if (item.endDays !== null && item.endDays !== undefined && item.endDays > 0) {
          // 如果startDays是0或不存在，只显示endDays；否则显示范围
          if (item.startDays === 0 || item.startDays === null || item.startDays === undefined) {
            days = `${item.endDays}天`;
          } else {
            days = `${item.startDays}-${item.endDays}天`;
          }
        } else if (item.startDays !== null && item.startDays !== undefined && item.startDays > 0) {
          days = `${item.startDays}天`;
        }
        
        if (days) {
          return `${days}后付款${percentage}%`;
        }
        return null;
      }).filter(item => item !== null);
      
      if (formattedItems.length === 0) {
        return '未设置';
      }
      
      return formattedItems.join('、');
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const statusMap = {
        'DRAFT': 'info',
        'PENDING_REVIEW': 'warning',
        'ACTIVE': 'success',
        'EXECUTING': 'warning',
        'SETTLED': 'success',
        'FINISHED': 'success',
        'TERMINATED': 'danger'
      };
      return statusMap[status] || 'info';
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        'DRAFT': '草稿',
        'PENDING_REVIEW': '待复核',
        'ACTIVE': '签订',
        'EXECUTING': '执行中',
        'SETTLED': '决算',
        'FINISHED': '收尾',
        'TERMINATED': '终止'
      };
      return statusMap[status] || status;
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      return moment(date).format('YYYY-MM-DD HH:mm:ss');
    },

    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      return parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 格式化单价（保留8位小数，去除末尾无意义的0）
    formatUnitPrice(amount) {
      if (!amount && amount !== 0) return '0';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0';
      // 先格式化为8位小数，然后去除末尾的0
      const formatted = num.toFixed(8);
      // 去除末尾的0和小数点（如果有）
      return formatted.replace(/\.?0+$/, '') || '0';
    },

    // 打印合同
    async printContract() {
      try {
        await this.performPrint();
      } catch (error) {
        console.error('打印失败:', error);
        this.$message.error('打印失败，请检查浏览器设置');
      }
    },
    
    // 执行打印
    async performPrint() {
      try {
        // 获取打印内容
        const printContent = this.$refs.printContent.innerHTML;
// 创建打印窗口
        const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        
        if (!printWindow) {
          this.$message.error('无法打开打印窗口，请检查浏览器弹窗设置');
          return;
        }
        
        // 写入打印内容
        printWindow.document.write(`
           <!DOCTYPE html>
           <html>
           <head>
             <title>合同打印 - ${this.contractInfo.contractName || '合同'}</title>
             <meta charset="utf-8">
             <style>
               body { 
                 font-family: Arial, "Microsoft YaHei", sans-serif; 
                 margin: 20px; 
                 font-size: 12px; 
                 line-height: 1.4;
                 color: #333;
               }
               .info-section { 
                 margin-bottom: 20px; 
                 border: 1px solid #ddd; 
                 padding: 15px; 
                 background-color: #fff;
               }
               .section-title { 
                 font-size: 16px; 
                 font-weight: bold; 
                 margin-bottom: 15px; 
                 border-bottom: 1px solid #ddd; 
                 padding-bottom: 10px; 
                 color: #333;
               }
               .info-item { 
                 margin-bottom: 10px; 
                 display: flex;
                 align-items: center;
                 font-size: 14px;
               }
               .info-item label { 
                 font-weight: bold; 
                 margin-right: 10px; 
                 min-width: 120px;
                 color: #606266;
                 font-size: 14px;
               }
               .info-item span {
                 flex: 1;
                 color: #333;
                 font-size: 14px;
               }
               
               /* 表格样式优化 */
               table { 
                 width: 100%; 
                 border-collapse: collapse; 
                 margin-top: 15px; 
                 font-size: 10px; 
                 table-layout: fixed; 
               }
               th, td { 
                 border: 1px solid #ddd; 
                 padding: 6px 8px; 
                 text-align: left; 
                 vertical-align: top;
               }
               th { 
                 background-color: #f5f7fa; 
                 font-weight: bold; 
                 color: #606266;
               }
               
               /* 确保表格在打印时不会被压缩 */
               .contract-items-table, .parties-table { 
                 width: 100% !important; 
                 table-layout: fixed !important;
               }
               
               /* 合同清单表格列宽优化 - 打印专用 */
               .contract-items-table th:nth-child(1), .contract-items-table td:nth-child(1) { width: 5%; } /* 序号 */
               .contract-items-table th:nth-child(2), .contract-items-table td:nth-child(2) { width: 12%; } /* 材料名称 */
               .contract-items-table th:nth-child(3), .contract-items-table td:nth-child(3) { width: 12%; } /* 规格型号 */
               .contract-items-table th:nth-child(4), .contract-items-table td:nth-child(4) { width: 10%; } /* 材质要求 */
               .contract-items-table th:nth-child(5), .contract-items-table td:nth-child(5) { width: 5%; } /* 单位 */
               .contract-items-table th:nth-child(6), .contract-items-table td:nth-child(6) { width: 6%; } /* 数量 */
               .contract-items-table th:nth-child(7), .contract-items-table td:nth-child(7) { width: 10%; } /* 不含税单价 */
               .contract-items-table th:nth-child(8), .contract-items-table td:nth-child(8) { width: 10%; } /* 含税单价 */
               .contract-items-table th:nth-child(9), .contract-items-table td:nth-child(9) { width: 10%; } /* 含税合价 */
               .contract-items-table th:nth-child(10), .contract-items-table td:nth-child(10) { width: 6%; } /* 税率 */
               .contract-items-table th:nth-child(11), .contract-items-table td:nth-child(11) { width: 14%; } /* 备注 */
               
               /* 变动价格式表格列宽 */
               .contract-items-table th:nth-child(1), .contract-items-table td:nth-child(1) { width: 5%; } /* 序号 */
               .contract-items-table th:nth-child(2), .contract-items-table td:nth-child(2) { width: 12%; } /* 材料名称 */
               .contract-items-table th:nth-child(3), .contract-items-table td:nth-child(3) { width: 12%; } /* 规格型号 */
               .contract-items-table th:nth-child(4), .contract-items-table td:nth-child(4) { width: 10%; } /* 材质要求 */
               .contract-items-table th:nth-child(5), .contract-items-table td:nth-child(5) { width: 5%; } /* 单位 */
               .contract-items-table th:nth-child(6), .contract-items-table td:nth-child(6) { width: 6%; } /* 数量 */
               .contract-items-table th:nth-child(7), .contract-items-table td:nth-child(7) { width: 12%; } /* 含税固定价 */
               .contract-items-table th:nth-child(8), .contract-items-table td:nth-child(8) { width: 12%; } /* 含税变动价 */
               .contract-items-table th:nth-child(9), .contract-items-table td:nth-child(9) { width: 10%; } /* 含税合价 */
               .contract-items-table th:nth-child(10), .contract-items-table td:nth-child(10) { width: 6%; } /* 税率 */
               .contract-items-table th:nth-child(11), .contract-items-table td:nth-child(11) { width: 14%; } /* 备注 */
               
               /* 参与方表格列宽优化 - 打印专用 */
               .parties-table th:nth-child(1), .parties-table td:nth-child(1) { width: 8%; } /* 序号 */
               .parties-table th:nth-child(2), .parties-table td:nth-child(2) { width: 15%; } /* 参与方类型 */
               .parties-table th:nth-child(3), .parties-table td:nth-child(3) { width: 45%; } /* 单位名称 */
               .parties-table th:nth-child(4), .parties-table td:nth-child(4) { width: 32%; } /* 备注 */
               
               /* 金额样式 */
               .amount-text, .total-amount { 
                 color: #333; 
                 font-weight: bold; 
               }
               
               /* 标签样式 */
               .el-tag { 
                 border: 1px solid #ddd; 
                 background-color: #fff; 
                 color: #333; 
                 padding: 2px 6px; 
                 font-size: 10px; 
                 border-radius: 3px;
               }
               
               /* 合计信息 */
               .total-section { 
                 margin-top: 15px; 
                 padding-top: 15px; 
                 border-top: 1px solid #ddd; 
                 text-align: right;
               }
               .total-item { 
                 margin-left: 20px; 
                 display: inline-block; 
                 font-weight: bold;
               }
               
               /* 空数据样式 */
               .no-data {
                 text-align: center;
                 padding: 20px;
                 color: #999;
               }
               
               @media print { 
                 body { margin: 0; }
                 .info-section { page-break-inside: avoid; }
                 table { page-break-inside: avoid; }
                 .el-button { display: none !important; }
               }
             </style>
           </head>
           <body>
             ${printContent}
           </body>
           </html>
        `);
        
        await installPlatformPrintFooter(printWindow, null, {
          documentType: 'CONTRACT',
          documentKey: this.contractId
        });
        printWindow.document.close();
        
        let printTriggered = false;
        let fallbackTimer = null;
        const triggerPrint = () => {
          if (printTriggered || printWindow.closed) return;
          printTriggered = true;
          if (fallbackTimer) clearTimeout(fallbackTimer);

          try {
            printWindow.focus();
            printWindow.print();
            
            // 打印完成后关闭窗口（延迟一点时间让用户看到打印对话框）
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          } catch (printError) {
            console.error('打印执行失败:', printError);
            this.$message.error('打印执行失败，请手动打印页面');
          }
        };

        // 优先等待窗口加载完成；兜底定时器仅在加载事件未触发时执行。
        printWindow.onload = triggerPrint;

        // 如果onload没有触发，直接尝试打印
        fallbackTimer = setTimeout(triggerPrint, 500);
        
      } catch (error) {
        console.error('打印过程出错:', error);
        this.$message.error('打印失败: ' + error.message);
      }
    },

    // 确认合同
     async confirmContract() {
      try {
        this.$confirm('确认后合同状态将变为执行中，是否继续？', '确认合同', {
           confirmButtonText: '确定',
           cancelButtonText: '取消',
           type: 'warning'
         }).then(async () => {
           this.loading = true;
           
           // 调用API更新合同状态为EXECUTING
           const response = await updateContractStatus(this.contractId, 'EXECUTING');
           
           if (response.data && response.data.success) {
             this.$message.success('合同确认成功，状态已更新为执行中');
             // 更新本地状态
             this.contractInfo.status = 'EXECUTING';
             // 重新获取合同详情
             this.fetchContractDetail();
           } else {
             this.$message.error(response.data?.message || '确认合同失败');
           }
         }).catch(() => {
           // 用户取消确认
         });
       } catch (error) {
         console.error('确认合同失败:', error);
         this.$message.error('确认合同失败: ' + (error.response?.data?.message || error.message || '未知错误'));
       } finally {
         this.loading = false;
       }
     },

     async review(approved) {
       if (!this.canReviewContract) {
         this.$message.error('只有该合同指定的复核人员可以进行复核');
         return;
       }
       let remark = '';
       if (!approved) {
         try {
           const result = await this.$prompt('请输入退回原因', '合同复核', { inputPlaceholder: '退回原因' });
           remark = result.value;
         } catch (e) { return; }
       }
       try {
         this.loading = true;
         const response = await reviewContract(this.contractId, approved, remark);
         if (response.data && response.data.success) {
           this.$message.success(approved ? '合同复核通过' : '合同已退回修改');
           await this.fetchContractDetail();
         } else {
           this.$message.error(response.data?.message || '合同复核失败');
         }
       } catch (error) {
         this.$message.error(error.response?.data?.message || error.message || '合同复核失败');
       } finally {
         this.loading = false;
       }
     },

     // 判断是否可以确认合同
     canConfirmContract() {
       // 检查合同状态是否为ACTIVE
       if (this.contractInfo.status !== 'ACTIVE') {
return false;
       }
       
       const currentUser = getUserInfo();
       const currentUnitId = currentUser?.unit_id || currentUser?.unitId;
       
       // 获取合同创建单位ID
       const contractUnitId = this.contractInfo.contractUnitId || 
                             (this.contractInfo.contractUnit && this.contractInfo.contractUnit.id);
       
       // 获取供应商单位ID
       let supplierUnitId = null;
       if (this.contractInfo.supplierUnitId) {
         supplierUnitId = this.contractInfo.supplierUnitId;
       } else if (this.contractInfo.supplierUnit && this.contractInfo.supplierUnit.id) {
         supplierUnitId = this.contractInfo.supplierUnit.id;
       } else if (this.contractInfo.contractParties && this.contractInfo.contractParties.length > 0) {
         const supplierParty = this.contractInfo.contractParties.find(party => party.partyType === 'SUPPLIER');
         if (supplierParty) {
           supplierUnitId = supplierParty.unitInfoId || (supplierParty.unitInfo && supplierParty.unitInfo.id);
         }
       }
       
      // 获取需求方单位ID列表
      const demandUnitIds = [];
      if (this.contractInfo.demandUnitIds && Array.isArray(this.contractInfo.demandUnitIds)) {
        demandUnitIds.push(...this.contractInfo.demandUnitIds);
      } else if (this.contractInfo.demandUnits && this.contractInfo.demandUnits.length > 0) {
        this.contractInfo.demandUnits.forEach(unit => {
          if (unit.id) {
            demandUnitIds.push(unit.id);
          }
        });
      } else if (this.contractInfo.contractParties && this.contractInfo.contractParties.length > 0) {
        const customerParties = this.contractInfo.contractParties.filter(party => party.partyType === 'CUSTOMER');
        customerParties.forEach(party => {
          const unitId = party.unitInfoId || (party.unitInfo && party.unitInfo.id);
          if (unitId) {
            demandUnitIds.push(unitId);
          }
        });
      }
      
// 判断创建单位是供应商还是需求方
      const isContractUnitSupplier = contractUnitId === supplierUnitId;
      const isContractUnitDemand = demandUnitIds.includes(contractUnitId);
      
// 如果创建单位是供应商，则需求方可以确认
      if (isContractUnitSupplier) {
        const canConfirm = demandUnitIds.includes(currentUnitId);
return canConfirm;
      }
      
      // 如果创建单位是需求方，则供应商可以确认
      if (isContractUnitDemand) {
        const canConfirm = currentUnitId === supplierUnitId;
return canConfirm;
      }
      
      // 如果无法判断创建单位的角色，使用默认逻辑：供应商可以确认
return currentUnitId === supplierUnitId;
    },

    // 返回
    goBack() {
      this.$router.go(-1);
    },
    
    // 脱敏姓名
    maskName(name) {
      return maskNameUtil(name);
    },
    
    // 脱敏电话
    maskPhone(phone) {
      return maskPhoneUtil(phone);
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-view-container {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 120px);
}

.print-content {
  /* 打印内容区域样式 */
  width: 100%;
}

/* 平时显示的表格样式 - 确保表格正常显示 */
.contract-items-table,
.parties-table {
  /* 平时显示时使用正常的表格布局 */
  width: 100%;
}

/* 平时显示时强制表格填满整行 */
.contract-items-table .el-table,
.parties-table .el-table {
  width: 100% !important;
}

.contract-items-table .el-table__body,
.parties-table .el-table__body {
  width: 100% !important;
}

.contract-items-table .el-table__header,
.parties-table .el-table__header {
  width: 100% !important;
}

/* 确保表格内容区域填满整行 */
.contract-items-table .el-table__body-wrapper,
.parties-table .el-table__body-wrapper {
  width: 100% !important;
}

.contract-items-table .el-table__header-wrapper,
.parties-table .el-table__header-wrapper {
  width: 100% !important;
}

/* 强制表格行填满整行 */
.contract-items-table .el-table__row,
.parties-table .el-table__row {
  width: 100% !important;
}

/* 确保表格单元格能自适应填满 */
.contract-items-table .el-table__cell,
.parties-table .el-table__cell {
  box-sizing: border-box;
}

/* 打印时的表格优化样式 - 只在打印时生效 */
@media print {
  .contract-items-table,
  .parties-table {
    /* 打印时确保表格在打印时不会被压缩 */
    table-layout: fixed !important;
    width: 100% !important;
    font-size: 10px !important;
  }
  
  .contract-items-table th,
  .contract-items-table td,
  .parties-table th,
  .parties-table td {
    padding: 3px 4px !important;
    font-size: 10px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  
  /* 确保表格内容完整显示 */
  .el-table__body-wrapper {
    overflow: visible !important;
  }
  
  .el-table__header-wrapper {
    overflow: visible !important;
  }
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-title {
  display: flex;
  align-items: center;
}

.title-mark {
  width: 4px;
  height: 20px;
  background-color: #409EFF;
  margin-right: 8px;
}

.header-title span {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.info-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 14px;
  
  label {
    font-weight: 500;
    color: #606266;
    min-width: 120px;
    margin-right: 10px;
    font-size: 14px;
  }
  
  span {
    color: #333;
    flex: 1;
    font-size: 14px;
  }
}

.amount-text {
  color: #f56c6c;
  font-weight: 500;
}

.variable-rates-section {
  margin-top: 20px;
  
  h4 {
    margin-bottom: 15px;
    color: #333;
  }
}

.total-section {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  
  .total-item {
    margin-left: 30px;
    display: flex;
    align-items: center;
    
    label {
      font-weight: 500;
      color: #606266;
      margin-right: 10px;
    }
    
    .total-amount {
      color: #f56c6c;
      font-weight: 500;
      font-size: 16px;
    }
  }
}

.no-data {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

/* 打印样式 */
@media print {
  .page-header {
    display: none;
  }
  
  .contract-view-container {
    padding: 0;
    background-color: #fff;
  }
  
  .info-section {
    box-shadow: none;
    border: 1px solid #ddd;
    margin-bottom: 15px;
    page-break-inside: avoid;
  }
  
  .el-button {
    display: none;
  }
  
  .el-tag {
    border: 1px solid #ddd;
    background-color: #fff;
    color: #333;
  }
  
  .el-table {
    border: 1px solid #ddd;
  }
  
  .el-table th,
  .el-table td {
    border: 1px solid #ddd;
  }
  
  .el-table th {
    background-color: #f5f7fa !important;
    color: #333 !important;
  }
  
  .amount-text,
  .total-amount {
    color: #333 !important;
  }
  
  /* 确保表格在打印时不会分页 */
  .el-table {
    page-break-inside: avoid;
  }
}

::v-deep .el-table th {
  background-color: #f5f7fa;
}

::v-deep .el-table--border {
  border-radius: 4px;
  overflow: hidden;
}
</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>
